import { useEffect, useMemo, useRef, useState } from 'react';
import { loadPersistedCrawlState, persistCrawlState } from '../lib/crawlPersistence';

export function createInitialProgress(maxPages) {
  return {
    current_page: 0,
    max_pages: maxPages,
    status: 'Démarrage...',
    pages_found: 0,
    duplicates_found: 0,
    pages_skipped: 0,
  };
}

function useCrawlUrlState(saved) {
  const [url, setUrl] = useState(saved.url || '');
  const [urlList, setUrlList] = useState(saved.urlList || '');
  const [isBatch, setIsBatch] = useState(saved.isBatch || false);
  const [isRecursive, setIsRecursive] = useState(saved.isRecursive || false);
  const [maxPages, setMaxPages] = useState(saved.maxPages || 200);
  const [urlPrefix, setUrlPrefix] = useState(saved.urlPrefix || '');
  const [forceRecrawl, setForceRecrawl] = useState(saved.forceRecrawl || false);
  return { url, setUrl, urlList, setUrlList, isBatch, setIsBatch, isRecursive, setIsRecursive, maxPages, setMaxPages, urlPrefix, setUrlPrefix, forceRecrawl, setForceRecrawl };
}

function useCrawlAuthState(saved) {
  const [authMode, setAuthMode] = useState(saved.authMode || 'credentials');
  const [username, setUsername] = useState(saved.username || '');
  const [password, setPassword] = useState(saved.password || '');
  const [googleEmail, setGoogleEmail] = useState(saved.googleEmail || '');
  const [googlePassword, setGooglePassword] = useState('');
  const [sessionCookies, setSessionCookies] = useState(saved.sessionCookies || '');
  const [rememberGoogleSession, setRememberGoogleSession] = useState(saved.rememberGoogleSession ?? true);
  const [useSavedGoogleSession, setUseSavedGoogleSession] = useState(saved.useSavedGoogleSession ?? true);
  const [showCookieHelp, setShowCookieHelp] = useState(false);
  return {
    authMode, setAuthMode, username, setUsername, password, setPassword,
    googleEmail, setGoogleEmail, googlePassword, setGooglePassword,
    sessionCookies, setSessionCookies, rememberGoogleSession, setRememberGoogleSession,
    useSavedGoogleSession, setUseSavedGoogleSession, showCookieHelp, setShowCookieHelp,
  };
}

function useCrawlProgressState() {
  const [crawling, setCrawling] = useState(false);
  const [progress, setProgress] = useState(null);
  const [completedResult, setCompletedResult] = useState(null);

  const progressPercentage = useMemo(
    () => (progress ? Math.round((progress.current_page / progress.max_pages) * 100) : 0),
    [progress]
  );

  return { crawling, setCrawling, progress, setProgress, completedResult, setCompletedResult, progressPercentage };
}

export function useCrawlFormState() {
  const savedRef = useRef(loadPersistedCrawlState());
  const urlState = useCrawlUrlState(savedRef.current);
  const authState = useCrawlAuthState(savedRef.current);
  const progressState = useCrawlProgressState();

  const combined = { ...urlState, ...authState, ...progressState };

  const persistedPayload = useMemo(() => ({
    url: urlState.url, urlList: urlState.urlList, isBatch: urlState.isBatch,
    isRecursive: urlState.isRecursive, maxPages: urlState.maxPages, urlPrefix: urlState.urlPrefix,
    forceRecrawl: urlState.forceRecrawl,
    authMode: authState.authMode, username: authState.username,
    googleEmail: authState.googleEmail, sessionCookies: authState.sessionCookies,
    rememberGoogleSession: authState.rememberGoogleSession,
    useSavedGoogleSession: authState.useSavedGoogleSession,
  }), [urlState.url, urlState.urlList, urlState.isBatch, urlState.isRecursive, urlState.maxPages,
       urlState.urlPrefix, urlState.forceRecrawl, authState.authMode, authState.username,
       authState.googleEmail, authState.sessionCookies, authState.rememberGoogleSession,
       authState.useSavedGoogleSession]);

  useEffect(() => {
    persistCrawlState(persistedPayload);
  }, [persistedPayload]);

  return combined;
}
